import React from "react"

const REFERENCE_LINE = /^\[\d+\]:\s+\S.*$/
const BULLET = /^[-*]\s+(.*)$/
const NUMBERED = /^\d+\.\s+(.*)$/
const HEADING = /^#{1,3}\s+(.*)$/
const INLINE =
  /\*\*([^*]+)\*\*|\*([^*\n]+)\*|`([^`]+)`|\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)|\[(\d+)\]/g

function renderInline(text: string, keyBase: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = []
  const pattern = new RegExp(INLINE.source, "g")
  let last = 0
  let index = 0
  let match = pattern.exec(text)
  while (match) {
    if (match.index > last) nodes.push(text.slice(last, match.index))
    const key = `${keyBase}-${index}`
    index += 1
    if (match[1] != null) {
      nodes.push(<strong key={key}>{renderInline(match[1], key)}</strong>)
    } else if (match[2] != null) {
      nodes.push(<em key={key}>{match[2]}</em>)
    } else if (match[3] != null) {
      nodes.push(<code key={key}>{match[3]}</code>)
    } else if (match[4] != null && match[5] != null) {
      nodes.push(
        <a key={key} href={match[5]} target="_blank" rel="noreferrer">
          {match[4]}
        </a>
      )
    } else if (match[6] != null) {
      nodes.push(<sup key={key}>{match[6]}</sup>)
    }
    last = match.index + match[0].length
    match = pattern.exec(text)
  }
  if (last < text.length) nodes.push(text.slice(last))
  return nodes
}

type ListBlock = { type: "ul" | "ol"; items: string[] }

const CopilotMessageBody: React.FC<{ text: string }> = ({ text }) => {
  const normalized = text
    .replace(/\r\n/g, "\n")
    .replace(/(\S)\*\*(?=[A-Za-z0-9])/g, "$1 **")
  const lines = normalized
    .split("\n")
    .filter((line) => !REFERENCE_LINE.test(line.trim()))

  const blocks: React.ReactNode[] = []
  let paragraph: string[] = []
  let list: ListBlock | null = null
  let count = 0

  const flushParagraph = () => {
    if (!paragraph.length) return
    const content = paragraph.join(" ").trim()
    paragraph = []
    if (!content) return
    const key = `p-${count}`
    count += 1
    blocks.push(<p key={key}>{renderInline(content, key)}</p>)
  }

  const flushList = () => {
    if (!list) return
    const current = list
    list = null
    const key = `l-${count}`
    count += 1
    const ListTag = current.type
    blocks.push(
      <ListTag key={key}>
        {current.items.map((item) => {
          const itemKey = `${key}-${count}`
          count += 1
          return (
            <li key={itemKey}>{renderInline(item, itemKey)}</li>
          )
        })}
      </ListTag>
    )
  }

  lines.forEach((raw) => {
    const line = raw.trim()
    if (!line) {
      flushParagraph()
      flushList()
      return
    }
    const heading = line.match(HEADING)
    const bullet = line.match(BULLET)
    const numbered = line.match(NUMBERED)
    if (heading) {
      flushParagraph()
      flushList()
      const key = `h-${count}`
      count += 1
      blocks.push(
        <p key={key}>
          <strong>{renderInline(heading[1], key)}</strong>
        </p>
      )
      return
    }
    if (bullet) {
      flushParagraph()
      if (!list || list.type !== "ul") {
        flushList()
        list = { type: "ul", items: [] }
      }
      list.items.push(bullet[1])
      return
    }
    if (numbered) {
      flushParagraph()
      if (!list || list.type !== "ol") {
        flushList()
        list = { type: "ol", items: [] }
      }
      list.items.push(numbered[1])
      return
    }
    flushList()
    paragraph.push(line)
  })
  flushParagraph()
  flushList()

  return <div className="copilot-message-body">{blocks}</div>
}

export default CopilotMessageBody
